// src/pages/CourseTime/components/SlideViewer.comp.jsx
import React from 'react';
import * as S from '../styles';
import SlideRenderer from './slides/SlideRenderer.comp';
import SessionAssessmentViewer from './SessionAssessmentViewer.comp';
import { GET_WEBSITE_LOCALIZE_TEXT, GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';
import left_arrow from '../../../assets/images/left-arrow.png';
import right_arrow from '../../../assets/images/right-arrow.png';

const SlideViewer = ({
  themeColor,
  sessionContent,
  activeSegment,
  currentSlideIndex,
  collapsed,
  pageLc,
  onPrevSlide,
  onNextSlide,
  isNextButtonDisabled,
  // Activity props
  journalingText,
  setJournalingText,
  shareWorkLink,
  setShareWorkLink,
  selectedPollOption,
  selectedPollOptions,
  setSelectedPollOption,
  setSelectedPollOptions,
  activityCompleted,
  interactionLoading,
  fetchingExistingRecord,
  inputsDisabled,
  onJournalingSave,
  onShareWorkSubmit,
  onPollSubmit,
  onAssessmentSubmit,
  onDiscussionStart,
  onEditActivity,
  assessmentAnswers,
  setAssessmentAnswers,
  // Additional props for poll results
  module_code,
  session,
  segment,
  assessmentScore,
  assessmentResultLoading,
  // Assessment props
  isAssessmentSegment,
  assessmentItems,
  segmentsWithAssessments,
  onAssessmentBack,
  onAssessmentFinish,
  // TTS props
  speaking, paused, setTtsAuto,
  startSpeech, pauseSpeech, stopSpeech,
  renderSpokenText, skipTitleSpeech,
  // TTS settings from parent
  ttsRate, setTtsRate,
  ttsPitch, setTtsPitch,
  ttsVolume, setTtsVolume,
  availableVoices, selectedVoiceURI, setSelectedVoiceURI,
}) => {
  // Local state for TTS Settings popover (must be before any early return)
  const [ttsSettingsAnchor, setTtsSettingsAnchor] = React.useState(null);
  const ttsSettingsOpen = Boolean(ttsSettingsAnchor);
  const handleToggleSettings = (e) => {
    if (ttsSettingsOpen) {
      setTtsSettingsAnchor(null);
    } else {
      setTtsSettingsAnchor(e.currentTarget);
    }
  };
  const handleCloseSettings = () => setTtsSettingsAnchor(null);

  // Assessments are now embedded as regular slides within segments
  if (!sessionContent || !activeSegment || !Array.isArray(sessionContent[activeSegment])) {
    return null;
  }

  const slides = sessionContent[activeSegment];
  const currentSlide = slides[currentSlideIndex];
  const isLastSlideOfSegment = currentSlideIndex === slides.length - 1;
  // Assessment intro slides carry their own CTA (see SlideRenderer) instead of
  // the floating nav arrows, since the top progress bar is hidden for them too.
  const isAssessmentIntroSlide = currentSlide?.SlideLayout === 'PRE_ASSESSMENT_INTRO'
    || currentSlide?.SlideLayout === 'POST_ASSESSMENT_INTRO';

  const isAssessmentSlide = currentSlide?.SlideLayout === 'ASSESSMENT_ACTIVITY';

  // Question numbering for ASSESSMENT_ACTIVITY slides (e.g. "Question 2 of 45")
  const assessmentSlideIndices = slides.reduce((indices, s, idx) => {
    if (s.SlideLayout === 'ASSESSMENT_ACTIVITY') indices.push(idx);
    return indices;
  }, []);
  const totalQuestions = assessmentSlideIndices.length;
  const questionNumber = assessmentSlideIndices.indexOf(currentSlideIndex) + 1;

  return (
    <>
      <S.PageWrapper>
        <S.SlideViewer>
          {currentSlideIndex > 0 && !isAssessmentIntroSlide && !isAssessmentSlide && (
            <S.SlideNavPrev
              position="prev"
              bg={themeColor}
              collapsed={collapsed}
              onClick={onPrevSlide}
              aria-label={GET_APP_LOCALIZE_TEXT(pageLc, 'ct__previous_slide')}
            >
              <S.SlideNavPrevImg src={left_arrow} />
            </S.SlideNavPrev>
          )}

          <S.SessionContent>
            <S.Slide>
              <SlideRenderer
                slide={currentSlide}
                journalingText={journalingText}
                setJournalingText={setJournalingText}
                shareWorkLink={shareWorkLink}
                setShareWorkLink={setShareWorkLink}
                selectedPollOption={selectedPollOption}
                selectedPollOptions={selectedPollOptions}
                setSelectedPollOption={setSelectedPollOption}
                setSelectedPollOptions={setSelectedPollOptions}
                activityCompleted={activityCompleted}
                interactionLoading={interactionLoading}
                fetchingExistingRecord={fetchingExistingRecord}
                inputsDisabled={inputsDisabled}
                onJournalingSave={onJournalingSave}
                onShareWorkSubmit={onShareWorkSubmit}
                onPollSubmit={onPollSubmit}
                onAssessmentSubmit={onAssessmentSubmit}
                onDiscussionStart={onDiscussionStart}
                onEditActivity={onEditActivity}
                assessmentAnswers={assessmentAnswers}
                setAssessmentAnswers={setAssessmentAnswers}
                module_code={module_code}
                session={session}
                segment={segment}
                slideIndex={currentSlideIndex}
                questionNumber={questionNumber}
                totalQuestions={totalQuestions}
                collapsed={collapsed}
                renderSpokenText={renderSpokenText}
                skipTitleSpeech={skipTitleSpeech}
                assessmentScore={assessmentScore}
                assessmentResultLoading={assessmentResultLoading}
                themeColor={themeColor}
                pageLc={pageLc}
                onNextSlide={onNextSlide}
                isNextButtonDisabled={isNextButtonDisabled || interactionLoading}
                onPrevSlide={onPrevSlide}
                isPrevButtonDisabled={currentSlideIndex === 0}
              />
                <S.TTSControls bg={themeColor}>
                    {/* Stop */}
                    <button
                        style={{
                            color: speaking ? '#fff' : '#DDDDDD',
                            width: 42,
                            height: 42,
                            border: 'none',
                            background: 'transparent',
                            cursor: speaking ? 'pointer' : 'not-allowed',
                        }}
                        onClick={() => {
                            if (!speaking) {
                                setTtsAuto(true);
                                startSpeech();
                            } else {
                                setTtsAuto(false);
                                stopSpeech();
                            }
                        }}
                        disabled={!speaking}
                        aria-label={speaking ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__stop_reading') : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__play_reading')}
                    >
                        <i className="fa fa-stop" style={{ fontSize: "21px" }} />
                    </button>

                    {/* Pause / Resume */}
                    <S.TTSPauseButton
                        themeColor={themeColor}
                        onClick={() => {
                            if (!speaking) {
                                setTtsAuto(true);
                                startSpeech();
                            } else {
                                pauseSpeech();
                            }
                        }}
                        aria-label={paused ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__resume_reading') : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__pause_reading')}
                    >
                        {speaking && !paused ? (
                            <i className="fa fa-pause" style={{ fontSize: "32px" }} />
                        ) : (
                            <i className="fa fa-play" style={{fontSize: "32px", marginLeft: "6px",}} />
                        )}
                    </S.TTSPauseButton>

                    {/* TTS Settings */}
                    <button
                        style={{
                            color: '#fff',
                            width: 42,
                            height: 42,
                            border: 'none',
                            transition: 'all 0.2s ease',
                            outline: 'none',
                            background: 'transparent',
                        }}
                        onClick={handleToggleSettings}
                        aria-label="TTS settings"
                    >
                        <i className="fa fa-cog" style={{ fontSize: '28px', }} />
                    </button>

                {ttsSettingsOpen && (
                   <S.PopoverWrapper>
                  <S.PopoverHeader>
                    <S.Title themeColor={themeColor}>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__voice_settings')}</S.Title>
                              <button
                                  onClick={handleCloseSettings}
                                  style={{
                                      background: "transparent",
                                      border: "none",
                                      color: themeColor || "#EE6D29",
                                      fontSize: "18px",
                                      cursor: "pointer",
                                  }}
                                  aria-label="Close settings"
                              >
                                  <i className="fa fa-times" />
                              </button>
                      </S.PopoverHeader>

                          <S.Label htmlFor="tts-voice">{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__select_voice')}</S.Label>
                      <S.SelectBox
                              id="tts-voice"
                        themeColor={themeColor}
                        value={selectedVoiceURI || ""}
                        onChange={(e) => setSelectedVoiceURI(e.target.value)}
                      >
                        <option value="">{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__system_default')}</option>
                        {(availableVoices || []).map((v) => (
                          <option key={v.voiceURI} value={v.voiceURI}>{`${v.name} (${v.lang})`}</option>
                        ))}
                      </S.SelectBox>

                    <S.RangeLabel>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__rate')}: {Number(ttsRate).toFixed(2)}</S.RangeLabel>
                    <S.Slider
                              themeColor={themeColor}
                              min="0.5"
                              max="2"
                              step="0.05"
                              value={ttsRate}
                              onChange={(e) => setTtsRate(parseFloat(e.target.value))}
                    />

                          <S.RangeLabel>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__pitch')}: {Number(ttsPitch).toFixed(2)}</S.RangeLabel>
                          <S.Slider
                              themeColor={themeColor}
                              min="0"
                              max="2"
                              step="0.05"
                              value={ttsPitch}
                              onChange={(e) => setTtsPitch(parseFloat(e.target.value))}
                    />

                          <S.RangeLabel>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__volume')}: {Number(ttsVolume).toFixed(2)}</S.RangeLabel>
                          <S.Slider
                              themeColor={themeColor}
                              min="0"
                              max="1"
                              step="0.05"
                              value={ttsVolume}
                              onChange={(e) => setTtsVolume(parseFloat(e.target.value))}
                    />
                  </S.PopoverWrapper>
                  )}
              </S.TTSControls>
            </S.Slide>
          </S.SessionContent>

          {!isAssessmentIntroSlide && !isAssessmentSlide && (
          <S.SlideNavNext
            position="next"
            onClick={isNextButtonDisabled || interactionLoading ? undefined : onNextSlide}
            aria-label={GET_APP_LOCALIZE_TEXT(pageLc, 'ct__next_slide')}
            bg={themeColor}
            style={{
              opacity: isNextButtonDisabled || interactionLoading ? 0.4 : 1,
              cursor: isNextButtonDisabled || interactionLoading ? 'not-allowed' : 'pointer',
              pointerEvents: isNextButtonDisabled || interactionLoading ? 'none' : 'auto'
            }}
          >
            {interactionLoading ? (
              <div style={{ fontSize: '24px', color: '#999' }}>
                <i className="fa fa-spinner fa-spin" />
              </div>
            ) : (
              <S.SlideNavNextImg src={right_arrow} style={{ opacity: isNextButtonDisabled ? 0.4 : 1 }} />
            )}
          </S.SlideNavNext>
          )}
        </S.SlideViewer>

        <S.Footer style={{marginTop:'auto', zIndex:'1000'}}>
          <div className={'dq-copyright'}>
            &copy; {new Date().getFullYear()} {GET_WEBSITE_LOCALIZE_TEXT(pageLc, 'home__dq_lab_pte_ltd_all_rights_reserved')}
          </div>
        </S.Footer>
      </S.PageWrapper>
    </>
  );
};

export default SlideViewer;
